import { type Hitbox, HitboxType } from "@common/utils/hitbox";
import { Assets, Color, type ColorSource, type FillStyle, type Graphics, Matrix, type Renderer, RendererType, Texture, type WebGLRenderer } from "pixi.js";
import { PIXI_SCALE } from "../utils/constants";
import { drawGroundGraphics } from "../utils/pixi";
import { FILL_FILES, FILL_TILE_UNITS, type FillMaterial, fillTint, GROUND_ATLAS, materialOf } from "./groundMaterialsLogic";

/*
    The ground's materials (0.98.7, the owner, 1 November 2026: "New ground textures"): the painted tiles
    (tools/ground/paint_ground.py, client/public/img/ground/) loaded once with the atlases, and the buildings' flat
    patches of ground drawn with them. A road is the asphalt tile tinted to the road's grey, repeated over its shape
    in the world's own measure (so the road's pieces join without a seam); a yard its dirt, a lot its concrete, a strip
    of beach its sand, a patch of lawn its grass. Each has its normal map, drawn the same way into the lighting's
    picture of the world's shapes (managers/normalLook.ts). Where the browser draws without a graphics card (Pixi's
    Canvas 2D fallback) the colours stay plain, as before.
*/

/** where the pictures are served from (client/public/img/ground/) */
const FOLDER = "./img/ground/";

class GroundMaterialsClass {
    private readonly _fills = new Map<FillMaterial, { albedo: Texture, normal: Texture }>();
    private _atlas?: { albedo: Texture, normal: Texture };
    private _loading?: Promise<void>;
    private _ready = false;
    private _plain = false;
    private _renderer?: Renderer;
    /** the textures on the card whose mips are clamped already (a lost context makes them again: clamped again) */
    private readonly _clamped = new WeakSet<WebGLTexture>();
    /** the pictures are loaded (the fills draw with them; before, or where they couldn't be loaded, a patch is its plain colour) */
    get ready(): boolean { return this._ready; }
    /** the natural ground's atlas (the ground shader's): its colours and its normals, when loaded */
    get atlas(): { readonly albedo: Texture, readonly normal: Texture } | undefined { return this._atlas; }

    /**
     * load every picture once (with the atlases, before the first map): a picture that fails leaves its material
     * plain, as the Canvas 2D fallback (`renderer`) keeps every patch
     */
    load(renderer: Renderer): Promise<void> {
        this._renderer = renderer;
        this._plain = renderer.type === RendererType.CANVAS;
        return this._loading ??= this._load();
    }

    private async _load(): Promise<void> {
        const one = async (name: string, repeat: boolean): Promise<Texture | undefined> => {
            try {
                const texture = await Assets.load<Texture>({
                    src: `${FOLDER}${name}.webp`,
                    // (the alpha channels carry occlusion and height, not cover: never premultiplied)
                    data: { alphaMode: "no-premultiply-alpha" }
                });
                const source = texture.source;
                if (repeat) source.addressMode = "repeat";
                source.autoGenerateMipmaps = true;
                source.scaleMode = "linear";
                source.update();
                return texture;
            } catch (e) {
                console.warn(`ground: ${name} couldn't be loaded`, e);
                return undefined;
            }
        };
        const [albedo, normal] = await Promise.all([one(GROUND_ATLAS.albedo, false), one(GROUND_ATLAS.normal, false)]);
        if (albedo && normal) {
            this._atlas = { albedo, normal };
            // (its mips clamped: on WebGPU by its sampler, the style's; on WebGL2 on the texture itself, `clampMips`)
            for (const texture of [albedo, normal]) {
                texture.source.style.lodMaxClamp = GROUND_ATLAS.mostLod;
                texture.source.style.update();
            }
            this.clampMips();
        }
        await Promise.all((Object.keys(FILL_FILES) as FillMaterial[]).map(async material => {
            const file = FILL_FILES[material];
            const [tile, bumps] = await Promise.all([one(file, true), one(`${file}_n`, true)]);
            if (tile && bumps) this._fills.set(material, { albedo: tile, normal: bumps });
        }));
        this._ready = true;
    }

    /**
     * the atlas's mips clamped at `GROUND_ATLAS.mostLod` on WebGL2 (0.98.7's audit: Pixi's WebGL leaves out the style's
     * clamp), on the textures the card has now: at the load, and each frame the ground is drawn (war/warLook.ts), so a
     * texture made again after a lost context is clamped again. WebGL1 has no clamp: its seams stay as they were
     */
    clampMips(): void {
        const renderer = this._renderer;
        const atlas = this._atlas;
        if (!renderer || !atlas || renderer.type !== RendererType.WEBGL) return;
        const webgl = renderer as WebGLRenderer;
        if (webgl.context.webGLVersion !== 2) return;
        const gl = webgl.gl;
        const textures = webgl.texture;
        for (const texture of [atlas.albedo, atlas.normal]) {
            const source = texture.source;
            if (source.destroyed) continue;
            const glTexture = textures.getGlSource(source);
            if (this._clamped.has(glTexture.texture)) continue;
            // (bound to unit 0 through Pixi, so its own record of what's bound where stays true)
            textures.bindSource(Texture.EMPTY.source, 0);
            textures.bindSource(source, 0);
            gl.texParameterf(glTexture.target, gl.TEXTURE_MAX_LOD, GROUND_ATLAS.mostLod);
            this._clamped.add(glTexture.texture);
        }
    }

    /** the fills draw with the pictures: loaded, and a renderer with a card (the Canvas 2D fallback keeps the plain colours) */
    get active(): boolean {
        return this._ready && this._fills.size > 0 && !this._plain;
    }

    /** a shape's extent (game units) for telling a patch from a marking */
    private _extent(hitbox: Hitbox): { width: number, height: number } {
        switch (hitbox.type) {
            case HitboxType.Rect: return { width: hitbox.max.x - hitbox.min.x, height: hitbox.max.y - hitbox.min.y };
            case HitboxType.Circle: return { width: hitbox.radius * 2, height: hitbox.radius * 2 };
            case HitboxType.Polygon: {
                const rect = hitbox.toRectangle();
                return { width: rect.max.x - rect.min.x, height: rect.max.y - rect.min.y };
            }
            case HitboxType.Group: {
                // (a group: its largest part says)
                let best = { width: 0, height: 0 };
                for (const part of hitbox.hitboxes) {
                    const extent = this._extent(part);
                    if (extent.width * extent.height > best.width * best.height) best = extent;
                }
                return best;
            }
        }
        return { width: 0, height: 0 };
    }

    /** a colour as Pixi takes it, as a number (one with an alpha of its own, "#rgba", is kept as it is: no material) */
    private _number(color: ColorSource): number | string {
        if (typeof color === "number") return color;
        if (typeof color === "string" && /^#[0-9a-f]{4}$/iu.test(color.trim())) return color;
        try {
            return new Color(color).toNumber();
        } catch {
            return 0x808080;
        }
    }

    /** the material a patch of ground of this colour and shape is drawn with, or none (a marking: its plain colour) */
    materialFor(color: ColorSource, hitbox: Hitbox): FillMaterial | undefined {
        if (!this.active) return undefined;
        const { width, height } = this._extent(hitbox);
        const material = materialOf(this._number(color), width, height);
        return material && this._fills.has(material) ? material : undefined;
    }

    /** the fill style of a material's tile, tinted to `color`, in the world's measure (pixels: `scale` pixels a unit) */
    style(material: FillMaterial, color: ColorSource, scale = PIXI_SCALE, normal = false): FillStyle | undefined {
        const fill = this._fills.get(material);
        if (!fill) return undefined;
        const texture = normal ? fill.normal : fill.albedo;
        const k = (FILL_TILE_UNITS[material] * scale) / texture.source.width;
        return {
            texture,
            matrix: new Matrix().scale(k, k),
            textureSpace: "global",
            color: normal ? 0xffffff : fillTint(material, this._number(color))
        };
    }

    /**
     * a patch of ground drawn into `graphics` (world coordinates, `scale` pixels a unit): its material's tile tinted to
     * its colour, or the plain colour. `normal` draws its normal map instead (flat blue where it's a marking)
     */
    fill(graphics: Graphics, hitbox: Hitbox, color: ColorSource, scale = PIXI_SCALE, normal = false): void {
        const material = this.materialFor(color, hitbox);
        graphics.beginPath();
        drawGroundGraphics(hitbox, graphics, scale);
        graphics.closePath();
        const style = material ? this.style(material, color, scale, normal) : undefined;
        if (style) graphics.fill(style);
        else graphics.fill(normal ? 0x8080ff : color);
    }
}

export const GroundMaterials = new GroundMaterialsClass();
